import { useEffect, useState, useMemo, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAppStore } from '../store/appStore';
import { filterVideo, formatIso8601Duration, getWatchedVideoIds, getWatchProgressByVideoId, orderVideosByFreshnessThenWatchStatus, type CategoryRow, type Video } from '@kidstube/shared';
import { applyFallbackThumbnail } from '../utils/thumbnailFallback';
import './HomePage.css';

type WatchState = 'new' | 'continue' | 'watched';

function getVideoWatchState(videoId: string, progressByVideoId: ReturnType<typeof getWatchProgressByVideoId>): WatchState {
    const progress = progressByVideoId.get(videoId);

    if (progress?.completed) return 'watched';
    if ((progress?.watchedDurationSeconds || 0) > 5) return 'continue';
    return 'new';
}

function getWatchStateLabel(state: WatchState): string {
    if (state === 'watched') return 'Watched';
    if (state === 'continue') return 'In-progress';
    return 'New';
}

const HOME_LIVE_REFRESH_COOLDOWN_MS = 6 * 60 * 60 * 1000;
const HOME_LIVE_REFRESH_STORAGE_KEY = 'kidstube-last-home-live-refresh';

function shouldRequestLiveHomeRefresh(refreshKey: number): boolean {
    if (refreshKey === 0) return false;

    const lastRefresh = Number(localStorage.getItem(HOME_LIVE_REFRESH_STORAGE_KEY) || '0');
    return !Number.isFinite(lastRefresh) || Date.now() - lastRefresh > HOME_LIVE_REFRESH_COOLDOWN_MS;
}

function markHomeLiveRefreshRequested(): void {
    localStorage.setItem(HOME_LIVE_REFRESH_STORAGE_KEY, String(Date.now()));
}

export function HomePage() {
    const [rows, setRows] = useState<CategoryRow[]>([]);
    const [loading, setLoading] = useState(true);
    const [refreshKey, setRefreshKey] = useState(0);
    const hasLoadedOnceRef = useRef(false);
    const { addDiscoveredVideos, allowlist, denylist, discoveredVideos, watchHistory } = useAppStore();
    const navigate = useNavigate();

    useEffect(() => {
        const controller = new AbortController();
        let refreshTimeout: ReturnType<typeof setTimeout> | null = null;
        setLoading(!hasLoadedOnceRef.current);

        const loadCachedHome = async (url: string, markLiveRefresh = false) => {
            const response = await fetch(url, {
                signal: controller.signal,
                credentials: 'include'
            });
            const data = await response.json();
            const nextRows = data.rows || [];
            const nextVideos = nextRows.flatMap((row: CategoryRow) => row.videos);

            if (markLiveRefresh && response.ok) {
                markHomeLiveRefreshRequested();
            }

            setRows(nextRows);
            addDiscoveredVideos(nextVideos);
            hasLoadedOnceRef.current = true;
            return data as { rows?: CategoryRow[]; refreshing?: boolean };
        };

        const requestLiveRefresh = shouldRequestLiveHomeRefresh(refreshKey);
        const homeUrl = requestLiveRefresh ? '/api/home?refresh=1&lazy=1' : '/api/home';

        loadCachedHome(homeUrl, requestLiveRefresh)
            .then(data => {
                if (data.refreshing) {
                    refreshTimeout = setTimeout(() => {
                        loadCachedHome('/api/home').catch(() => { });
                    }, 12000);
                }
            })
            .catch(() => { })
            .finally(() => setLoading(false));

        return () => {
            controller.abort();
            if (refreshTimeout) clearTimeout(refreshTimeout);
        };
    }, [addDiscoveredVideos, refreshKey]);

    // Get all videos, filter blocked, prioritize allowed
    const { allVideos } = useMemo(() => {
        const blockedChannelIds = new Set(denylist.filter(e => e.type === 'channel').map(e => e.id));
        const blockedVideoIds = new Set(denylist.filter(e => e.type === 'video').map(e => e.id));
        const allowedChannelIds = new Set(allowlist.filter(e => e.type === 'channel').map(e => e.id));
        const watchedVideoIds = getWatchedVideoIds(watchHistory);

        const favorites: Video[] = [];
        const others: Video[] = [];
        const seen = new Set<string>();

        const addCandidateVideo = (video: Video) => {
            if (seen.has(video.id)) return;
            seen.add(video.id);

            const channelId = video.snippet.channelId;
            if (blockedChannelIds.has(channelId) || blockedVideoIds.has(video.id)) return;
            if (!filterVideo(video, { allowlist, denylist, requireMadeForKids: true }).allowed) return;

            if (allowedChannelIds.has(channelId)) {
                favorites.push(video);
            } else {
                others.push(video);
            }
        };

        for (const row of rows) {
            for (const video of row.videos) {
                addCandidateVideo(video);
            }
        }

        for (const video of discoveredVideos) {
            addCandidateVideo(video);
        }

        return { allVideos: orderVideosByFreshnessThenWatchStatus([...favorites, ...others], watchedVideoIds) };
    }, [rows, allowlist, denylist, discoveredVideos, watchHistory]);

    const progressByVideoId = useMemo(() => getWatchProgressByVideoId(watchHistory), [watchHistory]);
    const featuredVideo = allVideos[0] || null;
    const listVideos = featuredVideo ? allVideos.slice(1) : allVideos;
    const featuredWatchState = featuredVideo ? getVideoWatchState(featuredVideo.id, progressByVideoId) : 'new';
    const featuredProgress = featuredVideo ? progressByVideoId.get(featuredVideo.id) : null;

    const handleVideoClick = (video: Video) => {
        navigate(`/watch/${video.id}`, {
            state: {
                video,
                channel: {
                    id: video.snippet.channelId,
                    title: video.snippet.channelTitle
                }
            }
        });
    };

    const refreshHome = () => {
        setRefreshKey(key => key + 1);
    };

    if (loading) {
        return (
            <div className="home-loading">
                <div className="home-loading__icon">🎬</div>
                <p>Loading videos...</p>
            </div>
        );
    }

    if (allVideos.length === 0) {
        return (
            <div className="home-empty">
                <span className="home-empty__icon">📺</span>
                <h2>No videos available</h2>
                <p>We could not load videos right now. Please try again.</p>
                <button onClick={() => window.location.reload()} className="home-empty__btn">
                    Retry
                </button>
            </div>
        );
    }

    return (
        <div className="home-page">
            {featuredVideo && (
                <section className="home-next" aria-label="Next video">
                    <button
                        type="button"
                        className="home-next__button"
                        data-video-id={featuredVideo.id}
                        data-watch-state={featuredWatchState}
                        aria-label={`${getWatchStateLabel(featuredWatchState)} video: ${featuredVideo.snippet.title}`}
                        onClick={() => handleVideoClick(featuredVideo)}
                    >
                        <div className="home-next__thumbnail">
                            <img
                                src={featuredVideo.snippet.thumbnails.high?.url || featuredVideo.snippet.thumbnails.medium?.url}
                                alt={featuredVideo.snippet.title}
                                loading="eager"
                                onError={event => applyFallbackThumbnail(event.currentTarget)}
                            />
                            <span className="home-next__play">▶</span>
                            {featuredVideo.contentDetails?.duration && (
                                <span className="home-list__duration">
                                    {formatIso8601Duration(featuredVideo.contentDetails.duration)}
                                </span>
                            )}
                            {featuredProgress && featuredProgress.ratio > 0 && !featuredProgress.completed && (
                                <span className="home-watch-progress" style={{ width: `${Math.round(featuredProgress.ratio * 100)}%` }} />
                            )}
                        </div>
                        <div className="home-next__info">
                            <h2>{featuredVideo.snippet.title}</h2>
                            <p>{featuredVideo.snippet.channelTitle}</p>
                        </div>
                    </button>
                </section>
            )}

            {/* Main video list - single column on mobile */}
            <div className="home-list">
                {listVideos.map(video => {
                    const watchState = getVideoWatchState(video.id, progressByVideoId);
                    const progress = progressByVideoId.get(video.id);

                    return (
                        <button
                            key={video.id}
                            className="home-list__item"
                            data-video-id={video.id}
                            data-watch-state={watchState}
                            aria-label={`${getWatchStateLabel(watchState)} video: ${video.snippet.title}`}
                            onClick={() => handleVideoClick(video)}
                        >
                            <div className="home-list__thumbnail">
                                <img
                                    src={video.snippet.thumbnails.high?.url || video.snippet.thumbnails.medium?.url}
                                    alt={video.snippet.title}
                                    loading="lazy"
                                    onError={event => applyFallbackThumbnail(event.currentTarget)}
                                />
                                {video.contentDetails?.duration && (
                                    <span className="home-list__duration">
                                        {formatIso8601Duration(video.contentDetails.duration)}
                                    </span>
                                )}
                                <span className="home-list__play" aria-hidden="true">▶</span>
                                {progress && progress.ratio > 0 && !progress.completed && (
                                    <span className="home-watch-progress" style={{ width: `${Math.round(progress.ratio * 100)}%` }} />
                                )}
                            </div>
                            <div className="home-list__info">
                                <div className="home-list__text">
                                    <h3 className="home-list__title">{video.snippet.title}</h3>
                                    <p className="home-list__channel">{video.snippet.channelTitle}</p>
                                </div>
                            </div>
                        </button>
                    );
                })}
            </div>

            {/* Bottom navigation */}
            <nav className="home-nav">
                <button type="button" onClick={refreshHome} className="home-nav__item active">
                    <span className="home-nav__icon">🏠</span>
                    Home
                </button>
                <Link to="/search" className="home-nav__item">
                    <span className="home-nav__icon">🔍</span>
                    Search
                </Link>
                <Link to="/parent" className="home-nav__item">
                    <span className="home-nav__icon">🔒</span>
                    Grown-up
                </Link>
            </nav>
        </div>
    );
}
